/* ============================================================
   PRO STUDIO → SHORTCUT "/"
   ============================================================
   Pelanggan simpan teks bernama (cth `/promptfashion1`). Di Workspace,
   taip "/" dalam popup Edit Prompts → pilih → teks masuk di tempat
   kursor (`ShortcutSlashPembantu.jsx`).

   ⭐ RUPA SAHAJA — prop-driven, corak ProStudioCtaSection. Tiada
      Firestore di dalam fail ini; pemanggil memberi data + tindakan,
      supaya skrin sama boleh dipakai dalam mod pelan tanpa menulis
      ke akaun sesiapa.

   🔒 `status` claim percuma ('admin'|'aktif'|'belum'|'tamat') —
      utils/ciriPercuma.js. Belum/tamat: senarai kekal dipapar + boleh
      dipadam, tetapi borang tambah + edit disorok.
   ============================================================ */

import { useState } from 'react';
import { Plus, Trash2, Check, X, Pencil, Lock } from 'lucide-react';

import { Card, CardBody, Btn, IconBtn, Badge } from '../design/ui';
import { SHORTCUT_MAX, SHORTCUT_TEKS_MAX, bersihkanNama, semakShortcut } from '../utils/shortcutStore';

export default function ProStudioShortcutSection({
    senarai = [],
    sudahMuat = false,
    ralatMuat = '',
    status = 'aktif',  // 'admin' | 'aktif' | 'belum' | 'tamat'
    onSimpan,          // ({id, nama, teks}) => Promise
    onPadam,           // (id) => Promise
    onAktifkan,        // buka popup claim
    t,
}) {
    const [namaBaru, setNamaBaru] = useState('');
    const [teksBaru, setTeksBaru] = useState('');
    const [sedangEdit, setSedangEdit] = useState(null);   // id
    const [namaEdit, setNamaEdit] = useState('');
    const [teksEdit, setTeksEdit] = useState('');
    const [sahPadam, setSahPadam] = useState(null);       // id
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');

    /* `t(kunci)` memulangkan nama kunci bila tiada — ganti dengan ayat lalai. */
    const T = (kunci, lalai, vars) => {
        if (!t) return lalai;
        const hasil = t(kunci, vars);
        return (!hasil || hasil === kunci) ? lalai : hasil;
    };

    const ayatRalat = (kod) => ({
        kosong: T('prostudio.sc.err.kosong', 'Isi nama dan teks dahulu.'),
        namaSalah: T('prostudio.sc.err.namaSalah', 'Nama hanya boleh mengandungi huruf kecil, nombor dan _ (2–40 aksara), tanpa jarak.'),
        namaWujud: T('prostudio.sc.err.namaWujud', 'Nama ini sudah digunakan. Sila pilih nama lain.'),
        terlaluPanjang: T('prostudio.sc.err.terlaluPanjang', `Teks terlalu panjang (maksimum ${SHORTCUT_TEKS_MAX} aksara).`, { n: SHORTCUT_TEKS_MAX }),
        terkunci: T('prostudio.sc.err.terkunci', 'Aktifkan Shortcut / percuma dahulu untuk menyimpan.'),
        hadPenuh: T('prostudio.sc.err.hadPenuh', `Had ${SHORTCUT_MAX} shortcut telah dicapai. Padam yang tidak digunakan dahulu.`, { n: SHORTCUT_MAX }),
        tiadaLagi: T('prostudio.sc.err.tiadaLagi', 'Shortcut ini sudah dipadam di browser lain.'),
        'permission-denied': T('prostudio.sc.err.ditolak', 'Simpanan ditolak. Sila refresh halaman dan cuba lagi.'),
    }[kod] || T('prostudio.sc.err.umum', 'Gagal menyimpan. Sila cuba lagi.'));

    const kodDari = (e) => e?.code || e?.message || '';

    const bolehTulis = status === 'admin' || status === 'aktif';
    const penuh = senarai.length >= SHORTCUT_MAX;

    const tambah = async () => {
        if (sibuk) return;
        const kod = semakShortcut({ nama: namaBaru, teks: teksBaru }, senarai, bolehTulis);
        if (kod) { setRalat(ayatRalat(kod)); return; }
        setSibuk(true); setRalat('');
        try {
            await onSimpan?.({ nama: namaBaru, teks: teksBaru });
            setNamaBaru(''); setTeksBaru('');
        } catch (e) {
            setRalat(ayatRalat(kodDari(e)));
        } finally { setSibuk(false); }
    };

    const simpanEdit = async (id) => {
        if (sibuk) return;
        const kod = semakShortcut({ id, nama: namaEdit, teks: teksEdit }, senarai, bolehTulis);
        if (kod) { setRalat(ayatRalat(kod)); return; }
        setSibuk(true); setRalat('');
        try {
            await onSimpan?.({ id, nama: namaEdit, teks: teksEdit });
            setSedangEdit(null);
        } catch (e) {
            setRalat(ayatRalat(kodDari(e)));
        } finally { setSibuk(false); }
    };

    const padam = async (id) => {
        setSibuk(true); setRalat('');
        try {
            await onPadam?.(id);
            setSahPadam(null);
        } catch (e) {
            setRalat(T('prostudio.sc.err.padam', 'Gagal memadam. Sila cuba lagi.'));
            console.warn('[Shortcut] padam gagal:', kodDari(e));
        } finally { setSibuk(false); }
    };

    const kotak = {
        width: '100%', boxSizing: 'border-box', height: 38, padding: '0 12px',
        borderRadius: 9, border: '1px solid var(--s-line-strong)',
        background: 'var(--s-fill)', color: 'var(--s-ink)',
        font: 'inherit', fontSize: 14,
    };
    const kotakTeks = { ...kotak, height: 'auto', minHeight: 96, padding: '10px 12px', lineHeight: 1.55, resize: 'vertical' };
    const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' };

    /* Input nama dengan "/" tetap di depan — pelanggan nampak bentuk yang
       akan ditaip nanti. Nilai dibersihkan semasa menaip (huruf besar →
       kecil, jarak → _), jadi apa yang kelihatan = apa yang disimpan. */
    const inputNama = (nilai, setNilai, extra = {}) => (
        <div style={{ position: 'relative' }}>
            <span style={{ ...mono, position: 'absolute', left: 12, top: 9, color: 'var(--s-ink-3)', fontSize: 14 }}>/</span>
            <input
                style={{ ...kotak, ...mono, paddingLeft: 24 }}
                value={nilai}
                maxLength={41}
                disabled={sibuk}
                placeholder="promptfashion1"
                onChange={(e) => { setNilai(bersihkanNama(e.target.value)); setRalat(''); }}
                {...extra}
            />
        </div>
    );

    return (
        <>
            <Card>
                <CardBody>
                    <p style={{ margin: '0 0 4px', fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)' }}>
                        {T('prostudio.sc.addTitle', 'Tambah shortcut')}
                    </p>
                    <p className="s-dim" style={{ margin: '0 0 12px', fontSize: 12.5, lineHeight: 1.55 }}>
                        {T('prostudio.sc.addHint',
                            'Di Workspace, buka Edit Prompts dan taip "/" untuk memilih shortcut. Teksnya akan dimasukkan di tempat kursor.')}
                    </p>

                    {status === 'belum' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <p className="s-dim" style={{ margin: 0, fontSize: 13, flex: '1 1 240px', lineHeight: 1.55 }}>
                                <Lock size={13} strokeWidth={2} style={{ marginRight: 6, verticalAlign: -2 }} />
                                {T('prostudio.sc.lockedBelum', 'Shortcut / percuma sehingga 30 November 2026. Aktifkan dahulu untuk mula menyimpan.')}
                            </p>
                            {onAktifkan && (
                                <Btn variant="primary" onClick={onAktifkan}>{T('ciri.activate', 'Aktifkan percuma')}</Btn>
                            )}
                        </div>
                    ) : status === 'tamat' ? (
                        <p className="s-dim" style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
                            <Lock size={13} strokeWidth={2} style={{ marginRight: 6, verticalAlign: -2 }} />
                            {T('prostudio.sc.lockedTamat', 'Tempoh percuma telah tamat. Shortcut anda masih disimpan dan boleh digunakan semula dengan pakej PRO Studio.')}
                        </p>
                    ) : penuh ? (
                        <p className="s-dim" style={{ margin: 0, fontSize: 13 }}>{ayatRalat('hadPenuh')}</p>
                    ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ maxWidth: 320 }}>
                                {inputNama(namaBaru, setNamaBaru)}
                            </div>
                            <textarea
                                style={kotakTeks}
                                value={teksBaru}
                                maxLength={SHORTCUT_TEKS_MAX}
                                disabled={sibuk}
                                placeholder={T('prostudio.sc.teksPlaceholder', 'Teks yang akan dimasukkan bila shortcut ini dipilih…')}
                                onChange={(e) => { setTeksBaru(e.target.value); setRalat(''); }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <p className="s-dim" style={{ margin: 0, fontSize: 11.5, flex: 1 }}>
                                    {teksBaru.length} / {SHORTCUT_TEKS_MAX}
                                </p>
                                <Btn variant="primary" icon={<Plus size={15} strokeWidth={2} />}
                                    disabled={sibuk} onClick={tambah}>
                                    {T('prostudio.sc.add', 'Simpan shortcut')}
                                </Btn>
                            </div>
                        </div>
                    )}

                    {ralat && (
                        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--s-stop)' }}>{ralat}</p>
                    )}
                </CardBody>
            </Card>

            <div style={{ height: 16 }} />

            {!sudahMuat ? (
                <p className="s-dim" style={{ fontSize: 13 }}>{T('prostudio.sc.loading', 'Memuatkan…')}</p>
            ) : ralatMuat ? (
                <p style={{ fontSize: 13, color: 'var(--s-stop)' }}>
                    {T('prostudio.sc.err.muat', 'Senarai shortcut tidak dapat dimuatkan. Sila refresh halaman.')}
                </p>
            ) : senarai.length === 0 ? (
                <p className="s-dim" style={{ fontSize: 13 }}>
                    {T('prostudio.sc.empty', 'Belum ada shortcut. Simpan yang pertama di atas.')}
                </p>
            ) : (
                <Card>
                    <CardBody style={{ padding: 0 }}>
                        {senarai.map((c, i) => {
                            const terkunci = !bolehTulis;
                            return (
                                <div key={c.id} style={{
                                    padding: '12px 16px',
                                    borderTop: i === 0 ? 'none' : '1px solid var(--s-line)',
                                }}>
                                    {sedangEdit === c.id ? (
                                        <div style={{ display: 'grid', gap: 8 }}>
                                            <div style={{ maxWidth: 320 }}>
                                                {inputNama(namaEdit, setNamaEdit, { autoFocus: true })}
                                            </div>
                                            <textarea
                                                style={kotakTeks}
                                                value={teksEdit}
                                                maxLength={SHORTCUT_TEKS_MAX}
                                                disabled={sibuk}
                                                onChange={(e) => { setTeksEdit(e.target.value); setRalat(''); }}
                                                onKeyDown={(e) => { if (e.key === 'Escape') setSedangEdit(null); }}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <p className="s-dim" style={{ margin: 0, fontSize: 11.5, flex: 1 }}>
                                                    {teksEdit.length} / {SHORTCUT_TEKS_MAX}
                                                </p>
                                                <Btn variant="ghost" icon={<X size={15} strokeWidth={2} />}
                                                    onClick={() => { setSedangEdit(null); setRalat(''); }}>
                                                    {T('prostudio.sc.cancel', 'Batal')}
                                                </Btn>
                                                <Btn variant="primary" icon={<Check size={15} strokeWidth={2} />}
                                                    disabled={sibuk} onClick={() => simpanEdit(c.id)}>
                                                    {T('prostudio.sc.save', 'Simpan')}
                                                </Btn>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ ...mono, fontSize: 13.5, color: terkunci ? 'var(--s-ink-3)' : 'var(--s-ink)' }}>/{c.nama}</div>
                                                <div className="s-dim" style={{
                                                    fontSize: 12.5, marginTop: 3,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }} title={c.teks}>{c.teks}</div>
                                            </div>
                                            {terkunci && (
                                                <Badge tone="neutral">
                                                    <Lock size={11} strokeWidth={2} style={{ marginRight: 4, verticalAlign: -1 }} />
                                                    {T('prostudio.sc.locked', 'Dikunci')}
                                                </Badge>
                                            )}
                                            {sahPadam === c.id ? (
                                                <>
                                                    <span style={{ fontSize: 12.5, color: 'var(--s-ink-2)', alignSelf: 'center' }}>
                                                        {T('prostudio.sc.confirmDelete', 'Padam shortcut ini?')}
                                                    </span>
                                                    <Btn variant="ghost" onClick={() => setSahPadam(null)}>{T('prostudio.sc.cancel', 'Batal')}</Btn>
                                                    <Btn variant="primary" disabled={sibuk} onClick={() => padam(c.id)}>{T('prostudio.sc.delete', 'Padam')}</Btn>
                                                </>
                                            ) : (
                                                <>
                                                    {!terkunci && (
                                                        <IconBtn icon={<Pencil size={15} strokeWidth={1.9} />}
                                                            label={T('prostudio.sc.edit', 'Edit')}
                                                            onClick={() => { setSedangEdit(c.id); setNamaEdit(c.nama || ''); setTeksEdit(c.teks || ''); setSahPadam(null); setRalat(''); }} />
                                                    )}
                                                    <IconBtn icon={<Trash2 size={15} strokeWidth={1.9} />}
                                                        label={T('prostudio.sc.delete', 'Padam')}
                                                        onClick={() => setSahPadam(c.id)} />
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </CardBody>
                </Card>
            )}

            <p className="s-dim" style={{ marginTop: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                {status === 'aktif'
                    ? T('prostudio.sc.footAktif', `Percuma sehingga 30 November 2026. Anda boleh menyimpan sehingga ${SHORTCUT_MAX} shortcut, dan ia sama di semua browser.`, { n: SHORTCUT_MAX })
                    : T('prostudio.sc.foot', `Anda boleh menyimpan sehingga ${SHORTCUT_MAX} shortcut. Shortcut disimpan pada akaun anda, jadi ia sama di semua browser.`, { n: SHORTCUT_MAX })}
            </p>
        </>
    );
}
